import { cn } from "@/lib/utils";
import React, { useEffect, useRef } from "react";
import { createNoise3D } from "simplex-noise";
import { motion } from "motion/react";
import { useTheme } from "next-themes";

interface VortexProps {
  children?: any;
  className?: string;
  containerClassName?: string;
  particleCount?: number;
  rangeY?: number;
  baseHue?: number;
  baseSpeed?: number;
  rangeSpeed?: number;
  baseRadius?: number;
  rangeRadius?: number;
  backgroundColor?: string;
}

export const Vortex = (props: VortexProps) => {
  const [isSafari, setIsSafari] = React.useState(false);
  const { resolvedTheme } = useTheme();
  // Store the current theme to prevent unwanted changes during resize
  const currentThemeRef = useRef<string | undefined>("dark");
  // Track theme changes for Safari-specific fixes
  const [themeChanged, setThemeChanged] = React.useState(false);

  // Store particle positions to prevent reset on theme change
  const particlePositionsRef = useRef<Float32Array | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isInitializedRef = useRef(false);

  // Store separate particle arrays for light and dark themes
  const darkParticlesRef = useRef<Float32Array | null>(null);
  const lightParticlesRef = useRef<Float32Array | null>(null);

  // Base particle count - we'll use more for light theme
  const baseParticleCount = props.particleCount || 700;
  // Change the lightThemeMultiplier to 2.2 for even more particles in the stripe
  const lightThemeMultiplier = 2.2; // Increased from 2.0 to 2.2 for more particles in light theme

  // Dynamic particle count based on theme and device size
  const getParticleCount = (isLight: boolean) => {
    // Large device particle counts
    return isLight
      ? Math.floor(baseParticleCount * lightThemeMultiplier)
      : baseParticleCount;
  };

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef(null);
  const animationFrameId = useRef<number>(null);
  const particleCount = props.particleCount || 700;
  const particlePropCount = 9;
  let particlePropsLength = getParticleCount(false) * particlePropCount;
  const rangeY = props.rangeY || 100;
  const baseTTL = 50;
  const rangeTTL = 150;
  const baseSpeed = props.baseSpeed || 0.0;
  const rangeSpeed = props.rangeSpeed || 1.5;
  const baseRadius = props.baseRadius || 1;
  const rangeRadius = props.rangeRadius || 2;
  const baseHue = props.baseHue || 220;
  const rangeHue = 100;
  const noiseSteps = 3;
  const xOff = 0.00125;
  const yOff = 0.00125;
  const zOff = 0.0005;
  let tick = 0;
  const noise3D = createNoise3D();
  let particleProps = new Float32Array(particlePropsLength);
  let center: [number, number] = [0, 0];

  const HALF_PI: number = 0.5 * Math.PI;
  const TAU: number = 2 * Math.PI;
  const TO_RAD: number = Math.PI / 180;
  const rand = (n: number): number => n * Math.random();
  const randRange = (n: number): number => n - rand(2 * n);
  const fadeInOut = (t: number, m: number): number => {
    let hm = 0.5 * m;
    return Math.abs(((t + hm) % m) - hm) / hm;
  };
  const lerp = (n1: number, n2: number, speed: number): number =>
    (1 - speed) * n1 + speed * n2;

  // Update the current theme reference when theme changes
  useEffect(() => {
    if (resolvedTheme) {
      const wasLight = currentThemeRef.current === "light";
      const isNowLight = resolvedTheme === "light";

      // If theme changed, we need to update particle count
      if (wasLight !== isNowLight) {
        // Set theme changed flag for Safari fix
        setThemeChanged(true);

        // Store current particles in the appropriate ref
        if (wasLight) {
          lightParticlesRef.current = particleProps.slice();
        } else {
          darkParticlesRef.current = particleProps.slice();
        }

        // Update particle count for new theme
        particlePropsLength = getParticleCount(isNowLight) * particlePropCount;

        // If we have stored particles for this theme, use them
        if (isNowLight && lightParticlesRef.current) {
          // If stored array is smaller than what we need, create a new one
          if (lightParticlesRef.current.length < particlePropsLength) {
            const newProps = new Float32Array(particlePropsLength);
            newProps.set(lightParticlesRef.current);
            particleProps = newProps;
            // Initialize the new particles
            for (
              let i = lightParticlesRef.current.length;
              i < particlePropsLength;
              i += particlePropCount
            ) {
              initParticle(i);
            }
          } else {
            particleProps = lightParticlesRef.current.slice(
              0,
              particlePropsLength
            );
          }
        } else if (!isNowLight && darkParticlesRef.current) {
          particleProps = darkParticlesRef.current.slice(
            0,
            particlePropsLength
          );
        } else {
          // If no stored particles, create new ones
          particleProps = new Float32Array(particlePropsLength);
          for (let i = 0; i < particlePropsLength; i += particlePropCount) {
            initParticle(i);
          }
        }

        // Update the ref for the current theme
        particlePositionsRef.current = particleProps.slice();

        // Force re-initialization of all particles to ensure they're within bounds
        // This fixes the issue with stray particles at the top of the screen
        resetAllParticles();
      }

      currentThemeRef.current = resolvedTheme;
    }
  }, [resolvedTheme]);

  // Function to reset all particles to ensure they're within bounds
  const resetAllParticles = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Reset all particles
    for (let i = 0; i < particlePropsLength; i += particlePropCount) {
      initParticle(i);
    }

    // Update refs
    particlePositionsRef.current = particleProps.slice();

    // Also update theme-specific refs
    if (currentThemeRef.current === "light") {
      lightParticlesRef.current = particleProps.slice();
    } else {
      darkParticlesRef.current = particleProps.slice();
    }
  };

  const setup = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (canvas && container) {
      const ctx = canvas.getContext("2d");

      if (ctx) {
        resize(canvas, ctx);

        // Only initialize particles if not already initialized
        if (!isInitializedRef.current) {
          initParticles();
          isInitializedRef.current = true;
        } else if (particlePositionsRef.current) {
          // Restore particle positions if we have them
          particleProps = new Float32Array(particlePositionsRef.current);
        }

        // Cancel any existing animation frame
        if (animationFrameRef.current !== null) {
          cancelAnimationFrame(animationFrameRef.current);
        }

        // Start a new animation loop
        draw(canvas, ctx);
      }
    }
  };

  const initParticles = () => {
    tick = 0;
    const isLightTheme = currentThemeRef.current === "light";
    particlePropsLength = getParticleCount(isLightTheme) * particlePropCount;
    particleProps = new Float32Array(particlePropsLength);

    for (let i = 0; i < particlePropsLength; i += particlePropCount) {
      initParticle(i);
    }

    // Store initial particle positions
    particlePositionsRef.current = particleProps.slice();

    // Also store in theme-specific refs
    if (isLightTheme) {
      lightParticlesRef.current = particleProps.slice();
    } else {
      darkParticlesRef.current = particleProps.slice();
    }
  };

  // Fix the initParticle function to keep particles in the central stripe for both themes
  const initParticle = (i: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let x, y, vx, vy, life, ttl, speed, radius, hue;

    // Ensure particles are initialized within the visible area
    // This prevents particles from appearing at the top of the screen
    x = rand(canvas.width);

    // Keep particles in the central stripe for both themes
    // But use a slightly wider range for light theme to increase density
    const isLightTheme = currentThemeRef.current === "light";
    if (isLightTheme) {
      // Slightly wider stripe for light theme, but still centered
      y = center[1] + randRange(rangeY * 1.2);
    } else {
      // Original distribution for dark theme
      y = center[1] + randRange(rangeY * 0.8);
    }

    // Ensure y is within the canvas bounds
    y = Math.max(0, Math.min(canvas.height, y));

    vx = 0;
    vy = 0;
    life = 0;
    ttl = baseTTL + rand(rangeTTL);
    speed = baseSpeed + rand(rangeSpeed);

    // For light theme, use slightly smaller particles to avoid performance issues
    // with the increased particle count
    radius = isLightTheme
      ? baseRadius * 0.9 + rand(rangeRadius * 0.9)
      : baseRadius + rand(rangeRadius);

    hue = baseHue + rand(rangeHue);

    particleProps.set([x, y, vx, vy, life, ttl, speed, radius, hue], i);
  };

  const draw = (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => {
    tick++;

    // Get the current theme from the ref to ensure consistency
    const isLightTheme = currentThemeRef.current === "light";

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Set background based on current theme
    ctx.fillStyle = isLightTheme ? "rgba(250, 250, 250, 0)" : "#000000";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw particles with appropriate blend mode
    if (isLightTheme) {
      // For light theme, we'll use a different approach
      drawLightThemeParticles(ctx, canvas);
    } else {
      // For dark theme, use the original approach
      drawDarkThemeParticles(ctx, canvas);
    }

    // Store the animation frame reference so we can cancel it later
    animationFrameRef.current = window.requestAnimationFrame(() =>
      draw(canvas, ctx)
    );

    // Store particle positions after each frame
    particlePositionsRef.current = particleProps.slice();

    // Also store in theme-specific refs
    if (isLightTheme) {
      lightParticlesRef.current = particleProps.slice();
    } else {
      darkParticlesRef.current = particleProps.slice();
    }
  };

  // Enhanced drawLightThemeParticles function with more pronounced fluorescent effect
  const drawLightThemeParticles = (
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement
  ) => {
    // Clear with white background first
    ctx.globalCompositeOperation = "screen";

    // Skip all glow effects for Safari in light theme
    if (isSafari) {
      // For Safari in light theme, just draw the black particles
      for (let i = 0; i < particlePropsLength; i += particlePropCount) {
        updateParticle(i, ctx, true);
      }
      return;
    }

    // For non-Safari browsers, continue with the original implementation
    // but adjust effects based on device size

    // Performance optimization for light theme with more particles:
    // Only draw the most visible glow effects for a portion of the particles
    // Adjust the effect percentage based on device size
    let effectPercentage = 0.7; // Default for large devices

    const fullEffectParticleCount = Math.floor(
      particlePropsLength * effectPercentage
    );

    // First glow pass - larger, colored glow for dramatic effect
    for (let i = 0; i < fullEffectParticleCount; i += particlePropCount) {
      const i2 = 1 + i,
        i3 = 2 + i,
        i4 = 3 + i,
        i5 = 4 + i,
        i6 = 5 + i,
        i7 = 6 + i,
        i8 = 7 + i;

      const x = particleProps[i];
      const y = particleProps[i2];
      const vx = particleProps[i3];
      const vy = particleProps[i4];
      const life = particleProps[i5];
      const ttl = particleProps[i6];
      const speed = particleProps[i7];
      const radius = particleProps[i8];
      const x2 = x + vx * speed;
      const y2 = y + vy * speed;




      // Adjust line width based on device size
      let lineWidthMultiplier = 5; // Default for large devices

      ctx.lineWidth = radius * lineWidthMultiplier;

      // Adjust opacity based on device size
      let opacityMultiplier = 0.8; // Default for large devices

      const opacity = fadeInOut(life, ttl) * opacityMultiplier;

      // Adjust color intensity based on device size
      let colorIntensity = 0; // Default for large devices

      ctx.strokeStyle = `rgba(0, 180, 255, ${opacity * colorIntensity})`;

      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.closePath();
      ctx.restore();
    }

    // Apply blur to the first glow - adjust based on device size
    ctx.save();

    let blurAmount, brightnessAmount, saturationAmount;
    blurAmount = 10;
    brightnessAmount = 180;
    saturationAmount = 150;

    ctx.filter = `blur(${blurAmount}px) brightness(${brightnessAmount}%) saturate(${saturationAmount}%)`;
    ctx.globalCompositeOperation = "screen";
    ctx.drawImage(canvas, 0, 0);
    ctx.restore();

    // Skip third glow pass on small and medium devices
    // Third glow pass - yellow/green for additional color dimension
    const thirdEffectParticleCount = Math.floor(particlePropsLength * 0.4);
    for (let i = 0; i < thirdEffectParticleCount; i += particlePropCount) {
      const i2 = 1 + i,
        i3 = 2 + i,
        i4 = 3 + i,
        i5 = 4 + i,
        i6 = 5 + i,
        i7 = 6 + i,
        i8 = 7 + i;

      const x = particleProps[i];
      const y = particleProps[i2];
      const vx = particleProps[i3];
      const vy = particleProps[i4];
      const life = particleProps[i5];
      const ttl = particleProps[i6];
      const speed = particleProps[i7];
      const radius = particleProps[i8];
      const x2 = x + vx * speed;
      const y2 = y + vy * speed;

      ctx.save();
      ctx.lineWidth = radius * 1;

      const opacity = fadeInOut(life, ttl) * 0.7;
      ctx.strokeStyle = `rgba(255, 255, 255, ${opacity * 0.3})`;

      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.closePath();
      ctx.restore();
    }

    // Apply blur to the third glow
    ctx.save();
    ctx.filter = "blur(4px) brightness(130%)";
    ctx.globalCompositeOperation = "color-dodge";
    ctx.drawImage(canvas, 0, 0);
    ctx.restore();

    // Fourth pass - actual particles with enhanced contrast
    ctx.globalCompositeOperation = "source-over";

    for (let i = 0; i < particlePropsLength; i += particlePropCount) {
      updateParticle(i, ctx, true);
    }

    // Final enhancement passes - adjust based on device size
    // Add contrast and vibrance
    ctx.save();
    ctx.globalCompositeOperation = "overlay";
    ctx.filter = "contrast(180%) brightness(120%) saturate(130%)";
    ctx.drawImage(canvas, 0, 0);
    ctx.restore();

  };

  // Separate function for dark theme particles with device-specific adjustments
  const drawDarkThemeParticles = (
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement
  ) => {
    // Adjust effects based on device size
    ctx.globalCompositeOperation = "lighter";

    for (let i = 0; i < particlePropsLength; i += particlePropCount) {
      updateParticle(i, ctx, false);
    }

    // Add glow effect - adjust based on device size
    ctx.save();
    ctx.globalCompositeOperation = "lighter";

    // Adjust blur and brightness based on device size
    let blurAmount, brightnessAmount;
    blurAmount = 3; // Reduced from 6px to 3px for less blur
    brightnessAmount = 150;

    ctx.filter = `blur(${blurAmount}px) brightness(${brightnessAmount}%)`;
    ctx.drawImage(canvas, 0, 0);
    ctx.restore();

    // Second pass - only for large devices, with reduced blur
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    ctx.filter = "brightness(120%)"; // No blur in second pass for sharper effect
    ctx.drawImage(canvas, 0, 0);
    ctx.restore();

    // Add a sharper highlight pass for large devices
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    ctx.filter = "contrast(120%)"; // Add contrast without blur for sharper effect
    ctx.drawImage(canvas, 0, 0);
    ctx.restore();
  };

  const updateParticle = (
    i: number,
    ctx: CanvasRenderingContext2D,
    isLightTheme: boolean
  ) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const i2 = 1 + i,
      i3 = 2 + i,
      i4 = 3 + i,
      i5 = 4 + i,
      i6 = 5 + i,
      i7 = 6 + i,
      i8 = 7 + i,
      i9 = 8 + i;
    let n, x, y, vx, vy, life, ttl, speed, x2, y2, radius, hue;

    x = particleProps[i];
    y = particleProps[i2];
    n = noise3D(x * xOff, y * yOff, tick * zOff) * noiseSteps * TAU;
    vx = lerp(particleProps[i3], Math.cos(n), 0.5);
    vy = lerp(particleProps[i4], Math.sin(n), 0.5);
    life = particleProps[i5];
    ttl = particleProps[i6];
    speed = particleProps[i7];
    x2 = x + vx * speed;
    y2 = y + vy * speed;
    radius = particleProps[i8];
    hue = particleProps[i9];

    drawParticle(x, y, x2, y2, life, ttl, radius, hue, ctx, isLightTheme);

    life++;

    particleProps[i] = x2;
    particleProps[i2] = y2;
    particleProps[i3] = vx;
    particleProps[i4] = vy;
    particleProps[i5] = life;

    // More aggressive bounds checking to prevent particles from escaping
    if (checkBounds(x, y, canvas) || life > ttl || y < 0 || y > canvas.height) {
      initParticle(i);
    }
  };

  // Enhanced drawParticle function
  const drawParticle = (
    x: number,
    y: number,
    x2: number,
    y2: number,
    life: number,
    ttl: number,
    radius: number,
    hue: number,
    ctx: CanvasRenderingContext2D,
    isLightTheme: boolean
  ) => {
    ctx.save();
    ctx.lineCap = "round";

    const opacity = fadeInOut(life, ttl);

    // Use different colors based on theme
    if (isLightTheme) {
      // For light theme, use a very dark color with increased thickness
      // For Safari in light theme, ensure we're using pure black with no color tint
      if (isSafari) {
        ctx.lineWidth = radius * 3;
        ctx.strokeStyle = `rgba(0, 0, 0, ${opacity * 0.95})`;
      } else {
        ctx.lineWidth = radius * 3;
        ctx.strokeStyle = `rgba(0, 0, 0, ${opacity * 0.95})`;
      }
    } else {
      // For dark theme, use pure white with adjusted opacity based on device size
      ctx.lineWidth = radius * 1.5;

      let opacityMultiplier;

      opacityMultiplier = 1.0;

      ctx.strokeStyle = `rgba(250, 250, 250, ${opacity * opacityMultiplier})`;
    }

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.closePath();
    ctx.restore();
  };

  const checkBounds = (x: number, y: number, canvas: HTMLCanvasElement) => {
    return x > canvas.width || x < 0 || y > canvas.height || y < 0;
  };

  const resize = (
    canvas: HTMLCanvasElement,
    ctx?: CanvasRenderingContext2D
  ) => {
    const container = canvas.parentElement;
    if (!container) return;

    const { width, height } = container.getBoundingClientRect();

    canvas.width = width;
    canvas.height = height;

    center[0] = 0.5 * width;
    center[1] = 0.5 * height;
  };

  // Enhanced Safari fix that works for both themes
  useEffect(() => {
    if (isSafari) {
      // Apply Safari fix on initial load
      applySafariFix();
    }
  }, [isSafari]);

  // Apply Safari fix when theme changes
  useEffect(() => {
    if (isSafari && themeChanged) {
      // Apply Safari fix on theme change
      applySafariFix();
      // Reset the theme changed flag
      setThemeChanged(false);
    }
  }, [isSafari, themeChanged]);

  // Function to apply Safari-specific fixes
  const applySafariFix = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.display = "none";
      requestAnimationFrame(() => {
        canvas.style.display = "";
      });
    }
  };

  // Initial setup
  useEffect(() => {
    setIsSafari(/^((?!chrome|android).)*safari/i.test(navigator.userAgent));
    setup();

    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (canvas && ctx) {
          // Save particle positions
          if (particleProps) {
            particlePositionsRef.current = particleProps.slice();

            if (currentThemeRef.current === "light") {
              lightParticlesRef.current = particleProps.slice();
            } else {
              darkParticlesRef.current = particleProps.slice();
            }
          }

          resize(canvas, ctx);
          setup();
        }
      }, 150); // debounce for 150ms
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Update when theme changes
  useEffect(() => {
    if (resolvedTheme) {
      setup();
    }
  }, [resolvedTheme]);

  // Get theme-specific filter settings for Safari
  const getSafariFilterStyle = () => {
    const isLightTheme = currentThemeRef.current === "light";

    if (isLightTheme) {
      // For Safari in light theme, use a filter that removes color effects
      // and only shows the black particles
      return {
        filter: "grayscale(100%) contrast(120%) brightness(100%)",
      };
    } else {
      return {
        filter: "blur(3px) brightness(650%)",
      };
    }
  };

    const handleResize = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) {
      resize(canvas, ctx);
    }
  };
 
  useEffect(() => {
    setup();
    window.addEventListener("resize", handleResize);
 
    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [])


  return (
    <div className={cn("relative h-full w-full bg-[#FAFAFA] dark:bg-black", props.containerClassName)}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        ref={containerRef}
        className="absolute inset-0 z-0 flex h-full w-full items-center justify-center bg-transparent"
        style={isSafari ? getSafariFilterStyle() : {}}
      >
        <canvas ref={canvasRef}></canvas>
      </motion.div>

      <div className={cn("relative -z-10 w-full h-full", props.className)}>
        {props.children}
      </div>
    </div>
  );
};
